import { AidaService, LlmForkService, LlmHistoryNode, LlmHistoryService } from '@adaptiva/ai/common';
import { IModalRef, ModalService } from '@adaptiva/core/modal';
import { PositioningService } from '@adaptiva/core/toggles';
import { ComponentRef, computed, DestroyRef, effect, EffectRef, EnvironmentInjector, inject, Injectable, Injector, signal, Signal, WritableSignal } from '@angular/core';
import { LlmHistoryNodeDetailComponent } from './details';
import { LlmHistoryNodeComponent } from './history-node';

@Injectable()
export class LlmHistoryComponentService {

	public readonly history: LlmHistoryService = inject(LlmHistoryService);

	public readonly forks: LlmForkService = inject(LlmForkService);

	public readonly modals: ModalService = inject(ModalService);

	public readonly positioning: PositioningService = inject(PositioningService);

	public readonly collapsedNodeIds: WritableSignal<Set<string>> = signal(new Set());

	public readonly displayTree: Signal<LlmHistoryNode> = computed(() => {
		return this.history.generateDisplayTree(this.history.tree(), this.collapsedNodeIds());
	});

	public readonly displayNodes: Signal<Map<string, LlmHistoryNode>> = computed(() => {
		return this.history.generateNodeMap(this.displayTree());
	});

	protected readonly destroyRef: DestroyRef = inject(DestroyRef);

	protected readonly modalRef: WritableSignal<IModalRef<LlmHistoryNodeDetailComponent>> = signal(null);

	protected readonly envInjector: EnvironmentInjector = inject(EnvironmentInjector);

	protected readonly injector: Injector = inject(Injector);

	protected readonly aida: AidaService = inject(AidaService);

	protected readonly closeModalFx: EffectRef = effect(() => {
		const _modalRef: ComponentRef<LlmHistoryNodeDetailComponent> = this.modalRef()?.ref;

		if (!_modalRef)
			return;

		if (!this.aida.isOpen()) {
			this.modals.remove(_modalRef.instance.node().nodeId);
			this.modalRef.set(null);
		}
	});

	public collapse(_node: LlmHistoryNode): void {
		this.collapsedNodeIds.update(x => new Set([...x, _node.nodeId]));
	}

	public expand(_node: LlmHistoryNode): void {
		this.collapsedNodeIds.update((_collapsed: Set<string>) => {
			_collapsed.delete(_node.nodeId);

			return new Set(_collapsed);
		});
	}

	public openDetails(_node: LlmHistoryNode, _componentMap: WeakMap<LlmHistoryNode, LlmHistoryNodeComponent>): void {
		const _component: LlmHistoryNodeComponent = _componentMap.get(_node);
		const _modalRef: IModalRef<LlmHistoryNodeDetailComponent> = this.modalRef();

		if (_modalRef)
			this.modals.remove(_modalRef.config.id);

		const _style: Record<string, string> = this.positioning.alignHorizontally(_component.elementRef.nativeElement, { horizontalOffset: 5 }, false);

		this.modals.create({
			type: LlmHistoryNodeDetailComponent,
			id: _node.nodeId,
			title: `Details: ${_node.userMessage}`,
			destroyRef: this.destroyRef,
			hasBackdrop: false,
			environmentInjector: this.envInjector,
			elementInjector: this.injector,
			inputs: {
				node: _node,
			},
			placement: {
				..._style,
				display: 'block',
				width: '400px',
				height: '320px',
			},
		}).subscribe({
			next: (_ref: IModalRef<LlmHistoryNodeDetailComponent>): void => {
				this.modalRef.set(_ref);

				this.modalRef().ref.onDestroy(() => {
					this.modalRef.set(null);
				});
			}
		});
	}

}
